// Tourvisio Media API Service
// Handles image fetching, processing, and AI tagging

export interface MediaAsset {
    id: string;
    filename: string;
    originalName: string;
    url: string;
    thumbnailUrl: string;
    mimeType: string;
    size: number;
    dimensions?: { width: number; height: number };
    folder: string;
    collection?: string;
    tags: string[];
    aiTags: string[];
    metadata: {
        alt: string;
        title: string;
        description: string;
        copyright: string;
        author: string;
    };
    stats: {
        views: number;
        downloads: number;
        shares: number;
    };
    versions: { id: string; url: string; createdAt: string; note: string }[];
    permissions: 'public' | 'private' | 'team';
    shareLink?: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

export interface Collection {
    id: string;
    name: string;
    description: string;
    coverImage?: string;
    assetCount: number;
    color: string;
    createdAt: string;
}

// Tourvisio Media Base URL
const TOURVISIO_MEDIA_BASE = 'http://service.stage.paximum.com/media';

// Image Processing Options
export interface ImageProcessingOptions {
    width?: number;
    height?: number;
    mode?: 'crop' | 'pad' | 'max' | 'min' | 'stretch' | 'boxpad';
    anchor?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
    bgcolor?: string;
    quality?: number;
}

// Build optimized image URL with processing parameters
export const buildMediaUrl = (path: string, options?: ImageProcessingOptions): string => {
    // If path is already a full URL, just add params
    const isFullUrl = path.startsWith('http://') || path.startsWith('https://');

    let url: URL;
    if (isFullUrl) {
        url = new URL(path);
    } else {
        // Make sure path is lowercase as per API requirements
        const cleanPath = path.toLowerCase();
        url = new URL(`${TOURVISIO_MEDIA_BASE}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`);
    }

    if (options) {
        if (options.width) url.searchParams.set('width', String(options.width));
        if (options.height) url.searchParams.set('height', String(options.height));
        if (options.mode) url.searchParams.set('mode', options.mode);
        if (options.anchor) url.searchParams.set('anchor', options.anchor);
        if (options.bgcolor) url.searchParams.set('bgcolor', options.bgcolor);
        if (options.quality) url.searchParams.set('quality', String(options.quality));
    }

    return url.toString();
};

// Generate thumbnail URL
export const getThumbnailUrl = (url: string, size: number = 300): string => {
    return buildMediaUrl(url, { width: size, height: size, mode: 'crop' });
};

// Generate full-size URL for preview
export const getPreviewUrl = (url: string, maxWidth: number = 1200): string => {
    return buildMediaUrl(url, { width: maxWidth, mode: 'max' });
};

// AI Tag Generation (simulated - ready for real AI API integration)
const AI_TAG_CATEGORIES = {
    destinations: ['beach', 'city', 'mountain', 'island', 'resort', 'historic', 'tropical', 'coastal'],
    nature: ['ocean', 'sky', 'sunset', 'sunrise', 'palm', 'forest', 'water', 'sand'],
    architecture: ['hotel', 'villa', 'building', 'modern', 'traditional', 'luxury', 'pool'],
    mood: ['romantic', 'adventure', 'relaxing', 'vibrant', 'peaceful', 'scenic'],
    activity: ['swimming', 'diving', 'sightseeing', 'dining', 'spa', 'golf']
};

export const generateAITags = async (imageUrl: string): Promise<string[]> => {
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // In production, this would call OpenAI Vision API, Google Cloud Vision, or similar
    // For now, generate contextual tags based on URL keywords and random selection
    const urlLower = imageUrl.toLowerCase();
    const generatedTags: string[] = [];

    // Analyze URL for context clues
    if (urlLower.includes('beach') || urlLower.includes('sea') || urlLower.includes('ocean')) {
        generatedTags.push('beach', 'ocean', 'coastal', 'relaxing');
    }
    if (urlLower.includes('hotel') || urlLower.includes('resort')) {
        generatedTags.push('hotel', 'resort', 'luxury', 'accommodation');
    }
    if (urlLower.includes('city') || urlLower.includes('urban')) {
        generatedTags.push('city', 'urban', 'modern', 'sightseeing');
    }
    if (urlLower.includes('mountain') || urlLower.includes('hill')) {
        generatedTags.push('mountain', 'nature', 'adventure', 'scenic');
    }
    if (urlLower.includes('sunset') || urlLower.includes('sunrise')) {
        generatedTags.push('sunset', 'sky', 'romantic', 'scenic');
    }

    // Add some random tags from categories
    Object.values(AI_TAG_CATEGORIES).forEach(category => {
        const randomIndex = Math.floor(Math.random() * category.length);
        if (Math.random() > 0.6) {
            generatedTags.push(category[randomIndex]);
        }
    });

    // Return unique tags
    return [...new Set(generatedTags)].slice(0, 8);
};

// Batch AI tagging for multiple images
export const batchGenerateAITags = async (imageUrls: string[]): Promise<Map<string, string[]>> => {
    const results = new Map<string, string[]>();

    for (const url of imageUrls) {
        const tags = await generateAITags(url);
        results.set(url, tags);
    }

    return results;
};

// API base URL
const API_BASE = '/api/media';

// Real API for fetching media assets from backend
export const fetchMediaAssets = async (options?: {
    collection?: string;
    folder?: string;
    search?: string;
    tags?: string[];
    type?: 'image' | 'video' | 'document';
    page?: number;
    limit?: number;
}): Promise<{ assets: MediaAsset[]; total: number; fromApi: boolean }> => {
    try {
        const params = new URLSearchParams();
        if (options?.page) params.set('page', String(options.page));
        if (options?.limit) params.set('limit', String(options.limit));
        if (options?.folder) params.set('folder', options.folder);
        if (options?.type) params.set('type', options.type);
        if (options?.search) params.set('search', options.search);

        const response = await fetch(`${API_BASE}?${params.toString()}`);

        if (!response.ok) {
            throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.media) {
            // Transform backend response to MediaAsset format
            const assets: MediaAsset[] = data.media.map((m: any) => ({
                id: m.id || m._id,
                filename: m.filename,
                originalName: m.originalName || m.title || m.filename,
                url: m.url,
                thumbnailUrl: m.thumbnailUrl || m.url,
                mimeType: m.mimeType,
                size: m.size || 0,
                dimensions: m.dimensions,
                folder: m.folder || 'general',
                collection: m.folder || 'general',
                tags: m.tags || [],
                aiTags: m.aiTags || [],
                metadata: {
                    alt: m.alt || '',
                    title: m.title || m.originalName || '',
                    description: m.caption || '',
                    copyright: m.copyright || '',
                    author: m.createdBy || ''
                },
                stats: m.stats || { views: 0, downloads: 0, shares: 0 },
                versions: m.versions || [],
                permissions: m.permissions || 'public',
                shareLink: m.shareLink,
                createdAt: m.createdAt,
                updatedAt: m.updatedAt,
                createdBy: m.createdBy || 'System'
            }));

            // Apply collection filter if provided (client-side backup)
            let filtered = assets;
            if (options?.collection) {
                filtered = filtered.filter(a => a.collection === options.collection || a.folder === options.collection);
            }

            return {
                assets: filtered,
                total: data.pagination?.total || filtered.length,
                fromApi: true
            };
        }

        throw new Error('Invalid API response');
    } catch (error) {
        console.warn('[MediaAPI] Backend unavailable, using fallback data:', error);

        // Fallback to mock data
        const mockAssets = getDefaultAssets();
        let filtered = [...mockAssets];

        if (options?.collection) {
            filtered = filtered.filter(a => a.collection === options.collection);
        }

        if (options?.search) {
            const q = options.search.toLowerCase();
            filtered = filtered.filter(a =>
                a.originalName.toLowerCase().includes(q) ||
                a.tags.some(t => t.toLowerCase().includes(q)) ||
                a.aiTags.some(t => t.toLowerCase().includes(q))
            );
        }

        if (options?.tags && options.tags.length > 0) {
            filtered = filtered.filter(a =>
                options.tags!.some(t => a.tags.includes(t) || a.aiTags.includes(t))
            );
        }

        return {
            assets: filtered,
            total: filtered.length,
            fromApi: false
        };
    }
};

// Upload media file to backend
export const uploadMediaFile = async (file: File, folder: string = 'general', metadata?: { alt?: string; title?: string }): Promise<MediaAsset | null> => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        if (metadata?.alt) formData.append('alt', metadata.alt);
        if (metadata?.title) formData.append('title', metadata.title);

        const response = await fetch(`${API_BASE}/upload`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Upload failed: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.media) {
            return data.media;
        }
        return null;
    } catch (error) {
        console.error('[MediaAPI] Upload error:', error);
        return null;
    }
};

// Delete media file
export const deleteMediaFile = async (id: string): Promise<boolean> => {
    try {
        const response = await fetch(`${API_BASE}/${id}`, { method: 'DELETE' });
        const data = await response.json();
        return data.success === true;
    } catch (error) {
        console.error('[MediaAPI] Delete error:', error);
        return false;
    }
};

// Update media metadata
export const updateMediaMetadata = async (id: string, updates: { alt?: string; title?: string; caption?: string; folder?: string; tags?: string[] }): Promise<MediaAsset | null> => {
    try {
        const response = await fetch(`${API_BASE}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates)
        });

        const data = await response.json();
        if (data.success && data.media) {
            return data.media;
        }
        return null;
    } catch (error) {
        console.error('[MediaAPI] Update error:', error);
        return null;
    }
};

// Get folders from backend
export const fetchFolders = async (): Promise<{ name: string; count: number }[]> => {
    try {
        const response = await fetch(`${API_BASE}/folders`);
        const data = await response.json();
        if (data.success && data.folders) {
            return data.folders;
        }
        return [
            { name: 'general', count: 0 },
            { name: 'destinations', count: 0 },
            { name: 'hotels', count: 0 }
        ];
    } catch {
        return [
            { name: 'general', count: 0 },
            { name: 'destinations', count: 0 },
            { name: 'hotels', count: 0 }
        ];
    }
};

// Create new folder
export const createFolder = async (folderName: string): Promise<boolean> => {
    try {
        const response = await fetch(`${API_BASE}/create-folder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folderName })
        });
        const data = await response.json();
        return data.success === true;
    } catch {
        return false;
    }
};

// ==========================================
// AI IMAGE PROCESSING
// ==========================================

export interface AIEnhanceOptions {
    brightness?: number;  // Default: 1.1
    contrast?: number;    // Default: 1.15
    saturation?: number;  // Default: 1.2
}

export type FilterType = 'bw' | 'sepia' | 'vivid' | 'cool' | 'warm' | 'vintage';

// Auto-enhance image using backend AI
export const enhanceImage = async (id: string, options?: AIEnhanceOptions): Promise<{ success: boolean; media?: MediaAsset; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/${id}/ai/enhance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(options || {})
        });

        const data = await response.json();

        if (data.success && data.media) {
            return { success: true, media: data.media };
        }

        return { success: false, error: data.error || 'Enhancement failed' };
    } catch (error) {
        console.error('[MediaAPI] Enhance error:', error);
        return { success: false, error: 'Network error during enhancement' };
    }
};

// Apply filter to image using backend
export const applyFilter = async (id: string, filter: FilterType): Promise<{ success: boolean; media?: MediaAsset; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/${id}/ai/filter`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filter })
        });

        const data = await response.json();

        if (data.success && data.media) {
            return { success: true, media: data.media };
        }

        return { success: false, error: data.error || 'Filter application failed' };
    } catch (error) {
        console.error('[MediaAPI] Filter error:', error);
        return { success: false, error: 'Network error during filter application' };
    }
};

// Remove background from image (requires backend API key configuration)
export const removeBackground = async (id: string): Promise<{ success: boolean; media?: MediaAsset; error?: string; configRequired?: boolean }> => {
    try {
        const response = await fetch(`${API_BASE}/${id}/ai/remove-background`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (data.success && data.media) {
            return { success: true, media: data.media };
        }

        return {
            success: false,
            error: data.error || 'Background removal failed',
            configRequired: data.configRequired
        };
    } catch (error) {
        console.error('[MediaAPI] Remove background error:', error);
        return { success: false, error: 'Network error during background removal' };
    }
};

// ==========================================
// FTP STORAGE BROWSER
// ==========================================

export interface FTPFile {
    name: string;
    size: number;
    isDirectory: boolean;
    modifiedAt: string;
    url: string | null;
    path: string;
}

export interface FTPHotelFolder {
    folderName: string;
    hotelId: string;
    hotelSlug: string;
    path: string;
}

export interface FTPStatus {
    success: boolean;
    connected: boolean;
    host: string;
    publicUrlBase: string;
}

// Get FTP connection status
export const getFTPStatus = async (): Promise<FTPStatus | null> => {
    try {
        const response = await fetch(`${API_BASE}/ftp/status`);
        const data = await response.json();
        if (data.success) {
            return data;
        }
        return null;
    } catch (error) {
        console.error('[MediaAPI] FTP status error:', error);
        return null;
    }
};

// Browse FTP folder
export const browseFTPFolder = async (ftpPath: string = 'uploads/hotels'): Promise<{ success: boolean; path: string; items: FTPFile[]; count: number }> => {
    try {
        const response = await fetch(`${API_BASE}/ftp/browse?path=${encodeURIComponent(ftpPath)}`);
        const data = await response.json();
        if (data.success) {
            return data;
        }
        return { success: false, path: ftpPath, items: [], count: 0 };
    } catch (error) {
        console.error('[MediaAPI] FTP browse error:', error);
        return { success: false, path: ftpPath, items: [], count: 0 };
    }
};

// Get FTP hotel folders
export const getFTPHotelFolders = async (): Promise<{ success: boolean; hotels: FTPHotelFolder[]; count: number }> => {
    try {
        const response = await fetch(`${API_BASE}/ftp/hotels`);
        const data = await response.json();
        if (data.success) {
            return data;
        }
        return { success: false, hotels: [], count: 0 };
    } catch (error) {
        console.error('[MediaAPI] FTP hotels error:', error);
        return { success: false, hotels: [], count: 0 };
    }
};

// Convert FTP file to MediaAsset format for display in grid
export const ftpFileToMediaAsset = (file: FTPFile, hotelName?: string): MediaAsset => {
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
    return {
        id: `ftp-${file.path.replace(/\//g, '-')}`,
        filename: file.name,
        originalName: file.name,
        url: file.url || '',
        thumbnailUrl: file.url || '',
        mimeType: isImage ? 'image/jpeg' : 'application/octet-stream',
        size: file.size,
        folder: 'ftp-storage',
        collection: hotelName || 'FTP',
        tags: ['ftp', 'hotel-image'],
        aiTags: [],
        metadata: {
            alt: file.name,
            title: file.name,
            description: `FTP: ${file.path}`,
            copyright: '',
            author: 'API Import'
        },
        stats: { views: 0, downloads: 0, shares: 0 },
        versions: [],
        permissions: 'public',
        createdAt: file.modifiedAt,
        updatedAt: file.modifiedAt,
        createdBy: 'System'
    };
};

// ==========================================
// UNIFIED FILE MANAGER API
// ==========================================

const FILE_MANAGER_API = '/api/file-manager';

export interface StorageSource {
    id: string;
    name: string;
    type: 'local' | 'ftp' | 'cloud';
    enabled: boolean;
    connected?: boolean;
    icon: string;
    description: string;
    host?: string;
    provider?: string;
}

export interface UnifiedFile {
    name: string;
    isDirectory: boolean;
    size: number;
    modifiedAt: string | null;
    path: string;
    url: string | null;
    source: string;
    thumbnail?: string;
}

// Get all available storage sources
export const getStorageSources = async (): Promise<{ success: boolean; sources: StorageSource[]; count: number }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/sources`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[FileManager] Sources error:', error);
        return { success: false, sources: [], count: 0 };
    }
};

// Browse files from any source
export const browseUnified = async (options: {
    source?: string;
    path?: string;
    search?: string;
}): Promise<{ success: boolean; source: string; path: string; items: UnifiedFile[]; count: number }> => {
    try {
        const params = new URLSearchParams();
        if (options.source) params.set('source', options.source);
        if (options.path) params.set('path', options.path);
        if (options.search) params.set('search', options.search);

        const response = await fetch(`${FILE_MANAGER_API}/browse?${params.toString()}`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[FileManager] Browse error:', error);
        return { success: false, source: options.source || 'local', path: options.path || '', items: [], count: 0 };
    }
};

// Search across all sources
export const searchFiles = async (query: string, source?: string): Promise<{ success: boolean; query: string; results: any[]; count: number }> => {
    try {
        const params = new URLSearchParams({ query });
        if (source) params.set('source', source);

        const response = await fetch(`${FILE_MANAGER_API}/search?${params.toString()}`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[FileManager] Search error:', error);
        return { success: false, query, results: [], count: 0 };
    }
};

// Upload file to specified destination
export const uploadToSource = async (
    file: File,
    options: { destination?: string; folder?: string }
): Promise<{ success: boolean; file?: any; error?: string }> => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (options.destination) formData.append('destination', options.destination);
        if (options.folder) formData.append('folder', options.folder);

        const response = await fetch(`${FILE_MANAGER_API}/upload`, {
            method: 'POST',
            body: formData
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Upload error:', error);
        return { success: false, error: 'Upload failed' };
    }
};

// Create folder
export const createFolderInSource = async (
    name: string,
    options: { source?: string; path?: string }
): Promise<{ success: boolean; folder?: any; error?: string }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/create-folder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name,
                source: options.source || 'local',
                path: options.path || ''
            })
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Create folder error:', error);
        return { success: false, error: 'Create folder failed' };
    }
};

// Delete file or folder
export const deleteFromSource = async (
    options: { source?: string; path?: string; id?: string }
): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/delete`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(options)
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Delete error:', error);
        return { success: false, error: 'Delete failed' };
    }
};

// Move file
export const moveFile = async (
    fromPath: string,
    toPath: string,
    source: string = 'local'
): Promise<{ success: boolean; from?: string; to?: string; message?: string; error?: string }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/move`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ source, fromPath, toPath })
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Move error:', error);
        return { success: false, error: 'Move failed' };
    }
};

// Copy file (can be between sources)
export const copyFile = async (
    fromPath: string,
    toPath: string,
    fromSource: string = 'local',
    toSource: string = 'local'
): Promise<{ success: boolean; from?: string; to?: string; message?: string; error?: string }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/copy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fromSource, toSource, fromPath, toPath })
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Copy error:', error);
        return { success: false, error: 'Copy failed' };
    }
};

// Sync files from FTP to local
export const syncFromFTP = async (
    syncPath: string = 'uploads/hotels'
): Promise<{ success: boolean; synced?: string[]; errors?: any[]; message?: string }> => {
    try {
        const response = await fetch(`${FILE_MANAGER_API}/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ from: 'ftp', to: 'local', path: syncPath })
        });
        return await response.json();
    } catch (error) {
        console.error('[FileManager] Sync error:', error);
        return { success: false, synced: [], errors: [], message: 'Sync failed' };
    }
};


// ==========================================
// STOCK PHOTOS API
// ==========================================

export interface StockImage {
    id: string;
    source: 'pexels';
    url: string;
    thumb: string;
    fullUrl: string;
    downloadUrl: string;
    alt: string;
    width: number;
    height: number;
    color?: string;
    credit: {
        name: string;
        username: string;
        link: string;
        photoLink: string;
    };
}

export interface StockSearchResult {
    source: string;
    total: number;
    totalPages: number;
    images: StockImage[];
}

export interface ArchivedStockImage {
    filename: string;
    url: string;
    source: string;
    size: number;
    downloadedAt: string;
    credit?: {
        name: string;
        username: string;
        link: string;
        photoLink: string;
    };
    alt?: string;
}

// Search stock photos from Pexels
export const searchStockPhotos = async (
    query: string,
    source: 'pexels' = 'pexels',
    options?: { page?: number; perPage?: number; orientation?: string }
): Promise<{ success: boolean; data?: StockSearchResult; error?: string }> => {
    try {
        const params = new URLSearchParams({
            query,
            source,
            page: String(options?.page || 1),
            perPage: String(options?.perPage || 12),
            orientation: options?.orientation || 'landscape'
        });

        const response = await fetch(`${API_BASE}/stock/search?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
            return { success: true, data: data.data };
        }
        return { success: false, error: data.error || 'Search failed' };
    } catch (error) {
        console.error('[MediaAPI] Stock search error:', error);
        return { success: false, error: 'Network error during stock search' };
    }
};

// Download stock photo to server archive
export const downloadStockPhoto = async (
    imageData: StockImage
): Promise<{ success: boolean; result?: ArchivedStockImage; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/stock/download`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                downloadUrl: imageData.downloadUrl,
                source: imageData.source,
                id: imageData.id,
                alt: imageData.alt,
                credit: imageData.credit
            })
        });

        const data = await response.json();

        if (data.success) {
            return { success: true, result: data.result };
        }
        return { success: false, error: data.error || 'Download failed' };
    } catch (error) {
        console.error('[MediaAPI] Stock download error:', error);
        return { success: false, error: 'Network error during download' };
    }
};

// Get archived stock photos
export const getStockArchive = async (
    source?: 'pexels'
): Promise<{ success: boolean; images?: ArchivedStockImage[]; total?: number; error?: string }> => {
    try {
        const params = source ? new URLSearchParams({ source }) : new URLSearchParams();
        const response = await fetch(`${API_BASE}/stock/archive?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
            return { success: true, images: data.images, total: data.total };
        }
        return { success: false, error: data.error || 'Failed to load archive' };
    } catch (error) {
        console.error('[MediaAPI] Stock archive error:', error);
        return { success: false, error: 'Network error loading archive' };
    }
};

// Delete stock photo from archive
export const deleteStockPhoto = async (
    filename: string,
    source: string = 'pexels'
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/stock/archive/${filename}?source=${source}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        return { success: data.success, error: data.error };
    } catch (error) {
        console.error('[MediaAPI] Stock delete error:', error);
        return { success: false, error: 'Network error during deletion' };
    }
};

// ==========================================
// AI IMAGE GENERATION (fal.ai)
// ==========================================

export interface AIGeneratedImage {
    filename: string;
    url: string;
    path: string;
    size: number;
    prompt: string;
    source: 'ai-generated';
}

export interface AIGenerationResult {
    success: boolean;
    prompt: string;
    model: string;
    images: AIGeneratedImage[];
    seed?: number;
}

// Generate AI image using fal.ai
export const generateAIImage = async (
    prompt: string,
    options?: { width?: number; height?: number; model?: string; num_images?: number }
): Promise<{ success: boolean; data?: AIGenerationResult; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/ai/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt,
                width: options?.width || 1024,
                height: options?.height || 768,
                model: options?.model || 'fal-ai/flux/schnell',
                num_images: options?.num_images || 1
            })
        });

        const data = await response.json();

        if (data.success) {
            return { success: true, data: data.data };
        }
        return { success: false, error: data.error || 'AI generation failed' };
    } catch (error) {
        console.error('[MediaAPI] AI generation error:', error);
        return { success: false, error: 'Network error during AI generation' };
    }
};

// Get AI generated images gallery
export const getAIGallery = async (): Promise<{ success: boolean; images?: AIGeneratedImage[]; total?: number; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/ai/gallery`);
        const data = await response.json();

        if (data.success) {
            return { success: true, images: data.images, total: data.total };
        }
        return { success: false, error: data.error || 'Failed to load AI gallery' };
    } catch (error) {
        console.error('[MediaAPI] AI gallery error:', error);
        return { success: false, error: 'Network error loading AI gallery' };
    }
};

// Delete AI generated image
export const deleteAIImage = async (filename: string): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await fetch(`${API_BASE}/ai/gallery/${filename}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        return { success: data.success, error: data.error };
    } catch (error) {
        console.error('[MediaAPI] AI delete error:', error);
        return { success: false, error: 'Network error during deletion' };
    }
};

// Get default assets with proper image URLs
export const getDefaultAssets = (): MediaAsset[] => [
    {
        id: '1',
        filename: 'antalya-beach.jpg',
        originalName: 'Antalya Beach Sunset',
        url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300',
        mimeType: 'image/jpeg',
        size: 2450000,
        dimensions: { width: 1920, height: 1080 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['beach', 'sunset', 'turkey', 'antalya'],
        aiTags: ['ocean', 'sky', 'sand', 'nature', 'travel'],
        metadata: { alt: 'Antalya beach at sunset', title: 'Antalya Beach', description: 'Beautiful sunset view', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 1250, downloads: 45, shares: 12 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-06-15',
        updatedAt: '2024-06-15',
        createdBy: 'Admin'
    },
    {
        id: '2',
        filename: 'bodrum-marina.jpg',
        originalName: 'Bodrum Marina View',
        url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300',
        mimeType: 'image/jpeg',
        size: 1890000,
        dimensions: { width: 1920, height: 1280 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['marina', 'boats', 'bodrum', 'aegean'],
        aiTags: ['water', 'yacht', 'harbor', 'summer', 'coastal'],
        metadata: { alt: 'Bodrum marina', title: 'Bodrum Marina', description: 'Yachts in Bodrum', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 890, downloads: 32, shares: 8 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-06-20',
        updatedAt: '2024-06-20',
        createdBy: 'Admin'
    },
    {
        id: '3',
        filename: 'maldives-resort.jpg',
        originalName: 'Maldives Luxury Resort',
        url: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=300',
        mimeType: 'image/jpeg',
        size: 3200000,
        dimensions: { width: 2400, height: 1600 },
        folder: 'hotels',
        collection: 'hotels',
        tags: ['maldives', 'resort', 'overwater', 'luxury'],
        aiTags: ['tropical', 'villa', 'ocean', 'paradise', 'bungalow'],
        metadata: { alt: 'Maldives overwater villa', title: 'Maldives Resort', description: 'Luxury overwater bungalow', copyright: '© Resort Partner', author: 'Resort Team' },
        stats: { views: 2100, downloads: 78, shares: 25 },
        versions: [],
        permissions: 'team',
        createdAt: '2024-05-10',
        updatedAt: '2024-05-10',
        createdBy: 'Admin'
    },
    {
        id: '4',
        filename: 'dubai-skyline.jpg',
        originalName: 'Dubai City Skyline',
        url: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=300',
        mimeType: 'image/jpeg',
        size: 2800000,
        dimensions: { width: 2000, height: 1333 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['dubai', 'skyline', 'city', 'modern'],
        aiTags: ['skyscraper', 'architecture', 'urban', 'night', 'luxury'],
        metadata: { alt: 'Dubai skyline at dusk', title: 'Dubai Skyline', description: 'Modern Dubai cityscape', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 1560, downloads: 56, shares: 18 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-04-25',
        updatedAt: '2024-04-25',
        createdBy: 'Admin'
    },
    {
        id: '5',
        filename: 'santorini-sunset.jpg',
        originalName: 'Santorini Blue Domes',
        url: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=300',
        mimeType: 'image/jpeg',
        size: 2150000,
        dimensions: { width: 1800, height: 1200 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['santorini', 'greece', 'sunset', 'romantic'],
        aiTags: ['church', 'dome', 'mediterranean', 'island', 'scenic'],
        metadata: { alt: 'Santorini blue domes', title: 'Santorini Sunset', description: 'Iconic Greek island view', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 1890, downloads: 67, shares: 22 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-03-15',
        updatedAt: '2024-03-15',
        createdBy: 'Admin'
    },
    {
        id: '6',
        filename: 'bali-temple.jpg',
        originalName: 'Bali Temple at Dawn',
        url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=300',
        mimeType: 'image/jpeg',
        size: 1950000,
        dimensions: { width: 1920, height: 1280 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['bali', 'temple', 'spiritual', 'indonesia'],
        aiTags: ['rice', 'terrace', 'culture', 'asia', 'nature'],
        metadata: { alt: 'Bali temple sunrise', title: 'Bali Temple', description: 'Traditional Balinese temple', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 1120, downloads: 41, shares: 15 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-02-28',
        updatedAt: '2024-02-28',
        createdBy: 'Admin'
    },
    {
        id: '7',
        filename: 'paris-eiffel.jpg',
        originalName: 'Paris Eiffel Tower',
        url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300',
        mimeType: 'image/jpeg',
        size: 2100000,
        dimensions: { width: 1920, height: 1280 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['paris', 'france', 'eiffel', 'europe'],
        aiTags: ['tower', 'landmark', 'romantic', 'city', 'iconic'],
        metadata: { alt: 'Eiffel Tower Paris', title: 'Paris', description: 'Iconic Eiffel Tower', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 2340, downloads: 89, shares: 34 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-01-20',
        updatedAt: '2024-01-20',
        createdBy: 'Admin'
    },
    {
        id: '8',
        filename: 'tokyo-shrine.jpg',
        originalName: 'Tokyo Sensoji Temple',
        url: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200',
        thumbnailUrl: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=300',
        mimeType: 'image/jpeg',
        size: 1780000,
        dimensions: { width: 1920, height: 1080 },
        folder: 'destinations',
        collection: 'destinations',
        tags: ['tokyo', 'japan', 'temple', 'asia'],
        aiTags: ['shrine', 'traditional', 'culture', 'lights', 'night'],
        metadata: { alt: 'Tokyo Sensoji Temple', title: 'Tokyo', description: 'Ancient temple in modern city', copyright: '© Lumi Tours', author: 'Travel Team' },
        stats: { views: 1670, downloads: 52, shares: 19 },
        versions: [],
        permissions: 'public',
        createdAt: '2024-01-10',
        updatedAt: '2024-01-10',
        createdBy: 'Admin'
    }
];

// Get default collections
export const getDefaultCollections = (): Collection[] => [
    { id: 'marketing', name: 'Marketing Assets', description: 'Brand and campaign materials', assetCount: 45, color: 'from-purple-500 to-indigo-600', createdAt: '2024-01-15' },
    { id: 'destinations', name: 'Destinations', description: 'Travel destination photos', assetCount: 128, color: 'from-cyan-500 to-blue-600', createdAt: '2024-02-20' },
    { id: 'hotels', name: 'Hotel Photos', description: 'Partner hotel imagery', assetCount: 89, color: 'from-amber-500 to-orange-600', createdAt: '2024-03-10' },
    { id: 'social', name: 'Social Media', description: 'Social media content', assetCount: 67, color: 'from-pink-500 to-rose-600', createdAt: '2024-04-05' },
];
